import React from "react";
import { styled } from "@mui/material/styles";
import { Box, Typography, Chip } from "@mui/material";
import NotificationIcon from "../../admin/components/notifications/NotificationIcon";
import ProfileMenu from "../profile/ProfileMenu";
import DocsIcon from "./DocsIcon";
import Icon from "./Icon";
import { getConfig } from "../../config";

import { createDocsLinkHandler } from "../../admin/utils/docsLinkUtils";
import useOverviewData from "../../admin/hooks/useOverviewData";
import { useEdition } from "../../admin/context/EditionContext";
import {
  StyledAppBar,
  StyledToolbar,
  NavigationContainer,
  LogoContainer,
  Logo,
  TabsContainer,
  StyledTabs,
  StyledTab,
  TabIndicatorProps,
} from "./styles";
import { withBase } from "../../runtimeConfig";

const LicenseAlert = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  marginRight: theme.spacing(10),
  border: '1px solid rgba(255, 255, 255, 0.32)',
  borderRadius: '8px',
  background: 'linear-gradient(90.01deg, rgba(3, 3, 28, 0.1) 32.62%, rgba(255, 255, 255, 0.1) 94.21%)',
  backdropFilter: 'blur(4px)',
  gap: theme.spacing(1),
}));

const TopNavigation = ({
  showAdmin,
  showChat,
  showPortal,
  currentTab,
  onTabChange,
  onLogout,
}) => {
  const { licenseDaysLeft, getDocsLink } = useOverviewData();
  const { version, isEnterprise } = useEdition();
  const config = getConfig();
  const tabs = [];

  if (showChat) {
    tabs.push({
      label: "Chat",
      value: "chat",
      dataTestId: "chat-tab",
    });
  }

  if (showPortal) {
    tabs.push({
      label: "AI Portal",
      value: "portal",
      dataTestId: "portal-tab",
    });
  }

  if (showAdmin) {
    tabs.push({
      label: "Administration",
      value: "admin",
      dataTestId: "admin-tab",
    });
  }

  return (
    <StyledAppBar position="fixed">
      <StyledToolbar>
        <NavigationContainer>
          <LogoContainer>
            <Logo
              src={withBase("/api/v1/branding/logo")}
              alt="Logo"
              title={version ? `Tyk AI Studio ${version}` : "Tyk AI Studio"}
              onError={(e) => {
                // Fallback to default logo if custom logo fails to load
                e.target.src = withBase("/logos/tyk-portal-logo.png");
              }}
            />
            <Chip
              label={isEnterprise ? "Enterprise Edition" : "Community Edition"}
              size="small"
              sx={{
                backgroundColor: isEnterprise ? "#FFD700" : "#424242",
                color: isEnterprise ? "#000" : "#fff",
                fontWeight: 600,
                fontSize: "0.7rem",
                height: "20px",
                marginLeft: 1,
              }}
            />
          </LogoContainer>
          <TabsContainer>
            <StyledTabs
              value={currentTab}
              onChange={(e, value) => onTabChange(value)}
              textColor="inherit"
              TabIndicatorProps={TabIndicatorProps}
            >
              {tabs.map((tab) => (
                <StyledTab
                  key={tab.value}
                  label={tab.label}
                  value={tab.value}
                  data-testid={tab.dataTestId}
                />
              ))}
            </StyledTabs>
          </TabsContainer>
        </NavigationContainer>
        {licenseDaysLeft && (
          <LicenseAlert>
            <Icon
              name="triangle-exclamation"
              sx={{
                color: "background.iconWarningDefault",
                width: 16,
                height: 16
              }}
            />
            <Typography variant="bodyLargeDefault" color="white">
              You have {licenseDaysLeft} days left in your trial
            </Typography>
            <Typography
              component="span"
              variant="bodyMediumSemiBold"
              color="text.linkDefault"
              sx={{ cursor: 'pointer' }}
              onClick={createDocsLinkHandler(getDocsLink, 'get_intouch_form')}
            >
              Get in touch
            </Typography>
          </LicenseAlert>
        )}
        {config?.docsEnabled && config?.docsURL && (
          <DocsIcon docsUrl={config.docsURL} />
        )}
        <NotificationIcon sx={{ mr: 1 }} />
        {/* Avatar with the account menu (profile, API key, preferences, log out). */}
        <ProfileMenu onLogout={onLogout} />
      </StyledToolbar>
    </StyledAppBar>
  );
};

export default TopNavigation;
